/**
 * ATL-688 — normalize the "cleared" marker for the three client loyalty/pricing fields.
 *
 * WHY THIS EXISTS (review finding, PR #380):
 * `ClientDetailScreen`'s bonus-level control is a free-text `Input`, so a user who CLEARS it
 * produces `''`, not `undefined` — and `handleSave` sends the field on every save. The dual-write
 * writes Firestore first and the backend second, so without normalization the two stores record
 * the same "cleared" intent differently:
 *
 *     Firestore: bonusLevel: ''        Supabase: bonus_level: NULL
 *
 * That is exactly the kind of silent divergence the ATL-688 compare gate was fixed to catch — it
 * would flag every cleared client as drift. `''` is also not a meaningful value for any of these
 * fields: an empty string is not a number, and it is not a valid `price_types.firestore_id`.
 *
 * So both stores record a cleared field the same way: **null**. This is the frontend half of the
 * contract the backend enforces (DTO `@EmptyStringToNull` + the service's `isClearedMarker`):
 *
 *     absent / undefined → leave the field alone
 *     null or ''         → explicitly cleared → null in BOTH stores
 *
 * Kept in its own module (rather than inside clients.ts) so it is unit-testable without pulling
 * in the Firebase/jotai/api-client import graph.
 */

import { normalizePhoneText } from '../../utils/phoneNumber'

/**
 * The fields whose edit controls can emit `''` for "cleared".
 *
 * Deliberately an explicit list rather than a blanket empty-string sweep over the whole payload:
 * `''` is a legitimate value for free-text fields like `notes` or `address` (clearing a note
 * should store an empty note, not null), so a blanket rule would corrupt them.
 */
export const CLEARABLE_LOYALTY_FIELDS = [
  'bonusLevel',
  'loyaltyDiscount',
  'preferredPriceType',
] as const

/** Is this the UI's "cleared" marker — an empty or whitespace-only string? */
export const isClearedMarker = (value: unknown): boolean =>
  typeof value === 'string' && value.trim() === ''

/**
 * Return a copy of `payload` with any cleared ('' / whitespace-only) loyalty field turned into
 * `null`. Keys that are absent stay absent — "leave alone" must not become "clear".
 *
 * The `as T` is sound rather than a shortcut: the shape is unchanged and only ever has a `''`
 * replaced by `null`, which every one of these fields already admits (`?: ... | null` on both
 * CreateClientParams and UpdateClientParams). TypeScript simply cannot track a write made
 * through an index signature back onto the concrete interface `T`.
 */
export const normalizeClearedLoyaltyFields = <T extends object>(payload: T): T => {
  const result: Record<string, unknown> = { ...(payload as Record<string, unknown>) }

  for (const field of CLEARABLE_LOYALTY_FIELDS) {
    if (field in result && isClearedMarker(result[field])) {
      result[field] = null
    }
  }

  return result as T
}

/**
 * ATL-1523 — the client's phone fields, as they are stored in BOTH stores: invisible format
 * characters removed and Unicode spaces made plain (`normalizePhoneText`). Applied where the other
 * client-field normalization is, before the Firestore write and the dual-write, so the two stores
 * record the same text. Keys that are absent stay absent; a non-string value is left as it is.
 */
export const CLIENT_PHONE_FIELDS = ['phone', 'phoneNumber'] as const

export const normalizeClientPhoneFields = <T extends object>(payload: T): T => {
  const result: Record<string, unknown> = { ...(payload as Record<string, unknown>) }
  for (const field of CLIENT_PHONE_FIELDS) {
    if (field in result) result[field] = normalizePhoneText(result[field])
  }
  // Same soundness argument as normalizeClearedLoyaltyFields: the shape is unchanged, and a string
  // field only ever receives a string.
  return result as T
}

